import { PublicShell } from "@layout/PublicShell";
import { Shell } from "@layout/Shell";
import { createBrowserRouter } from "react-router";

import { NotFound } from "@screens/NotFound";

import { RouteHydrateFallback } from "@features/RouteHydrateFallback";

import { clearSession } from "@storage/session";
import { TUserAuthData } from "@storage/session/types";
import { appTokenStorage, userAuthDataStorage } from "@storage/sessionStorage";

import { ROUTES } from "@configs/routes";

const sessionLoader = async () => {
	const app_token = appTokenStorage.getItem();
	const userData = userAuthDataStorage.getItemAsJson<TUserAuthData>();

	if (!app_token || userData === null) {
		clearSession();
		window.location.replace(ROUTES.LOGIN);
	}

	return { currentAccount: userData, app_token };
};

export const router = createBrowserRouter([
	{
		path: "/",
		Component: PublicShell,
		HydrateFallback: RouteHydrateFallback,
		children: [
			{
				index: true,
				path: ROUTES.LOGIN,
				lazy: async () => {
					const { LoginScreen } = await import("@screens/Login/LoginScreen");
					return { Component: LoginScreen };
				},
			},
			{
				path: ROUTES.RESET_PASSWORD,
				lazy: async () => {
					const { PasswordRecoveryScreen } = await import("@screens/PasswordRecovery/PasswordRecoveryScreen");
					return { Component: PasswordRecoveryScreen };
				},
			},
			{
				path: ROUTES.RESET_PASSWORD_FORM,
				lazy: async () => {
					const { ResetPasswordScreen } = await import("@screens/ResetPassword/ResetPasswordScreen");
					return { Component: ResetPasswordScreen };
				},
			},
			{
				path: ROUTES.SIGN_UP,
				lazy: async () => {
					const { CreateAccountScreen } = await import("@screens/CreateAccount/CreateAccountScreen");
					return { Component: CreateAccountScreen };
				},
			},
			{
				path: ROUTES.CHECK_SIGNUP_URL,
				lazy: async () => {
					const { CheckApplicationStatusScreen } =
						await import("@screens/CheckApplicationStatus/CheckApplicationStatusScreen");
					return { Component: CheckApplicationStatusScreen };
				},
			},
			{
				path: "*",
				Component: NotFound,
			},
		],
	},
	{
		loader: sessionLoader,
		shouldRevalidate: () => true,
		Component: Shell,
		HydrateFallback: RouteHydrateFallback,
		children: [
			{
				path: ROUTES.HOME,
				loader: sessionLoader,
				lazy: async () => {
					const { ChatScreen } = await import("@screens/Chat/ChatScreen");
					return { Component: ChatScreen };
				},
			},
			{
				path: ROUTES.PROFILE_SETTINGS,
				lazy: async () => {
					const { ProfileScreen } = await import("@screens/Profile/ProfileScreen");
					return { Component: ProfileScreen };
				},
			},
			{
				path: ROUTES.AGE_VERIFICATION,
				lazy: async () => {
					const { AgeVerificationScreen } = await import("@screens/AgeVerification/AgeVerificationScreen");
					return { Component: AgeVerificationScreen };
				},
			},
			{
				path: ROUTES.INTEGRATIONS,
				lazy: async () => {
					const { IntegrationsScreen } = await import("@screens/Integrations/IntegrationsScreen");
					return { Component: IntegrationsScreen };
				},
			},
			{
				path: ROUTES.VR_VIDEO,
				lazy: async () => {
					const { VrVideoScreen } = await import("@screens/VrVideo/VrVideoScreen");
					return { Component: VrVideoScreen };
				},
			},
			{
				path: ROUTES.NOTES,
				lazy: async () => {
					const { RemarkScreen } = await import("@screens/Remarks/RemarkScreen");
					return { Component: RemarkScreen };
				},
			},
			{
				path: ROUTES.SHOW_SETTINGS,
				lazy: async () => {
					const { ShowSettingsScreen } = await import("@screens/ShowSettings/ShowSettingsScreen");
					return { Component: ShowSettingsScreen };
				},
			},
			{
				path: ROUTES.EARNINGS,
				lazy: async () => {
					const { EarningsScreen } = await import("@screens/Earnings/EarningsScreen");
					return { Component: EarningsScreen };
				},
			},
			{
				path: ROUTES.BROADCAST_SETTINGS,
				lazy: async () => {
					const { BroadcastSettingsScreen } = await import("@screens/BroadcastSettings/BroadcastSettingsScreen");
					return { Component: BroadcastSettingsScreen };
				},
			},
			{
				path: ROUTES.BLACKLIST,
				lazy: async () => {
					const { BlacklistScreen } = await import("@screens/Blacklist/BlacklistScreen");
					return { Component: BlacklistScreen };
				},
			},
			{
				path: ROUTES.PRIVACY_SETTINGS,
				lazy: async () => {
					const { PrivacySettingsScreen } = await import("@screens/PrivacySettings/PrivacySettingsScreen");
					return { Component: PrivacySettingsScreen };
				},
			},
			{
				path: ROUTES.PAYMENT_SETTINGS,
				loader: sessionLoader,
				lazy: async () => {
					const { PaymentSettingsScreen } = await import("@screens/PaymentSettings/PaymentSettingsScreen");
					return { Component: PaymentSettingsScreen };
				},
			},
			{
				path: ROUTES.POSTS,
				lazy: async () => {
					const { PostsScreen } = await import("@screens/Posts/PostsScreen");
					return { Component: PostsScreen };
				},
			},
			{
				path: "*",
				Component: NotFound,
			},
		],
	},
]);
